import DashboardCard from 'components/DashboardCard';
import React from 'react';
import { average, sum } from 'shared/constants/math';
import { EDIT_LICENSE, EDIT_MATOMO } from 'shared/constants/permissions';
import dayjs from 'dayjs';
import useDataSource from 'hooks/useDataSource';
import { Module } from 'shared/prisma/types';
import { hasModuleLicense } from 'shared/utils/license';
import CenterContent from 'components/CenterContent';
import { Button } from 'antd';
import { Link } from 'react-router-dom';

function getStats(website) {
  const visitStats = website?.visitStats || [];
  return [
    { title: 'Visitors', value: sum(visitStats, { valueSelector: v => v.visitors }), chartData: visitStats.map(s => s.visitors) },
    { title: 'Visits', value: sum(visitStats, { valueSelector: v => v.visits }), chartData: visitStats.map(s => s.visits) },
    { title: 'Page views', value: sum(visitStats, { valueSelector: v => v.actions }), chartData: visitStats.map(s => s.actions) },
    { title: 'Goal conversions', value: sum(website?.matomoGoalMetrics, { valueSelector: g => g.conversions }), chartData: website?.matomoGoalMetrics.map(s => s.conversions || 0) },
    { title: 'Goal conversion rate', value: average(website?.matomoGoalMetrics, { valueSelector: g => g.conversionRate, precision: 1 }) || 0, suffix: '%', chartData: website?.matomoGoalMetrics.map(s => s.conversionRate || 0) },
  ];
}

const extraFields = `
  website {
    id
    trackVisitors
    matomoId
    allVisitStats {       
      visits
      actions
      visitors
    }
    visitStats(startDate: "$startDate", endDate: "$endDate") {
      date
      visits
      actions
      visitors
    }
    matomoGoalMetrics(period: "day", date: "last30") {
      id
      conversions
      conversionRate
    }
  }
`;

const AnalyticsCard = () => {
  const { dataSource, dataSourceId, loading, hasDataSourcePermission } = useDataSource({ extraFields: extraFields.replace('$startDate', dayjs().subtract(29, 'days').format('YYYY-MM-DD')).replace('$endDate', dayjs().add(1, 'day').format('YYYY-MM-DD')) });
  const canEditMatomo = hasDataSourcePermission(EDIT_MATOMO);
  const website = dataSource?.website;
  const websiteStats = getStats(website);

  const hasAnalytics = hasModuleLicense(dataSource?.activeLicense, Module.Analytics);

  let cardContent = null;
  let cardProps = {};

  if (hasAnalytics) {
    cardProps = {
      statistics: websiteStats,
      resultsUrl: `/data-sources/${dataSourceId}/analytics`,
      settingsUrl: canEditMatomo && `/data-sources/${dataSourceId}/analytics/settings/tracking`,
      settingsLabel: 'Analytics settings',
    };
  } else {
    const canEditLicense = hasDataSourcePermission(EDIT_LICENSE);
    cardContent = (
      <CenterContent>
        <p>
          Your license does not include analytics.
        </p>
        {canEditLicense && (
          <Link to={`/data-sources/${dataSourceId}/settings/license`}>
            <Button type="primary">
              Upgrade license
            </Button>
          </Link>
        )}
      </CenterContent>
    );
  }

  return (
    <DashboardCard
      module={Module.Analytics}
      loading={loading}
      title="Analytics"
      period="Last 30 days"
      name="analytics"
      disabled={!hasAnalytics}
      {...cardProps}
    >
      {cardContent}
    </DashboardCard>
  );
};

export default AnalyticsCard;
